/**
 * Retrieves the translation of text.
 *
 * @see https://developer.wordpress.org/block-editor/reference-guides/packages/packages-i18n/
 */
import { __ } from '@wordpress/i18n';
import { InspectorControls } from '@wordpress/block-editor';
import { PanelBody, TextControl, ToggleControl } from '@wordpress/components';
import { useState, useEffect } from '@wordpress/element';
import './editor.scss';

/**
 * The edit function describes the structure of your block in the context of the
 * editor. This represents what the editor will render when the block is used.
 *
 * @see https://developer.wordpress.org/block-editor/reference-guides/block-api/block-edit-save/#edit
 *
 * @return {Element} Element to render.
 */
export default function Edit({ attributes, setAttributes }) {
    const [settings, setSettings] = useState({
        enabled: 'no',
        title: __('Pay by Card', 'youseeme'),
        description: __('Pay securely using your credit or debit card', 'youseeme'),
        api_endpoint: window.youseemeCardData?.api_endpoint || '',
        merchant_name: window.youseemeCardData?.merchant_name || ''
    });

    // Load settings on component mount
    useEffect(() => {
        if (window.youseemeCardData) {
            setSettings({
                enabled: window.youseemeCardData.enabled || 'no',
                title: window.youseemeCardData.title || __('Pay by Card', 'youseeme'),
                description: window.youseemeCardData.description || 
                            __('Pay securely using your credit or debit card', 'youseeme'),
                api_endpoint: window.youseemeCardData.api_endpoint || '',
                merchant_name: window.youseemeCardData.merchant_name || ''
            });
        }
    }, []);

    // Update settings in parent window when they change
    const updateSetting = (key, value) => {
        const newSettings = { ...settings, [key]: value };
        setSettings(newSettings);
        
        // Update the global settings object
        if (window.youseemeCardData) {
            window.youseemeCardData[key] = value;
        }
    };

    return (
        <div className="youseeme-card-payment-block">
            <InspectorControls>
                <PanelBody title={__('Payment Settings', 'youseeme')}>
                    <ToggleControl
                        label={__('Enable/Disable', 'youseeme')}
                        help={__('Enable or disable this payment method', 'youseeme')}
                        checked={settings.enabled === 'yes'}
                        onChange={(value) => updateSetting('enabled', value ? 'yes' : 'no')}
                    />
                    
                    <TextControl
                        label={__('Title', 'youseeme')}
                        value={settings.title}
                        onChange={(value) => updateSetting('title', value)}
                        help={__('Payment method title that the customer will see during checkout.', 'youseeme')}
                    />
                    
                    <TextControl
                        label={__('Description', 'youseeme')}
                        value={settings.description}
                        onChange={(value) => updateSetting('description', value)}
                        help={__('Payment method description that the customer will see during checkout.', 'youseeme')}
                    />
                    
                    <TextControl
                        label={__('API Endpoint', 'youseeme')}
                        value={settings.api_endpoint}
                        onChange={(value) => updateSetting('api_endpoint', value)}
                        placeholder="https://api.youseeme.io/payments"
                        help={__('Enter the payment gateway API endpoint URL', 'youseeme')}
                    />
                    
                    <TextControl
                        label={__('Merchant Name', 'youseeme')}
                        value={settings.merchant_name}
                        onChange={(value) => updateSetting('merchant_name', value)}
                        help={__('The name that will be displayed on the payment button', 'youseeme')}
                    />
                </PanelBody>
            </InspectorControls>

            <div className="youseeme-card-payment-preview">
                <div className="wc_payment_method payment_method_youseeme_card">
                    <input
                        id="payment_method_youseeme_card"
                        className="input-radio"
                        type="radio"
                        name="payment_method"
                        value="youseeme_card"
                        checked={true}
                        readOnly
                    />
                    <label htmlFor="payment_method_youseeme_card">
                        {settings.title || __('Pay by Card', 'youseeme')}
                    </label>
                    <div className="payment_box payment_method_youseeme_card">
                        <p>{settings.description || __('Pay securely using your credit or debit card', 'youseeme')}</p>
                        
                        <div className="youseeme-payment-button-container">
                            <button 
                                type="button" 
                                className="button alt"
                                id="youseeme-pay-button"
                                onClick={(e) => {
                                    e.preventDefault();
                                    
                                    if (!settings.api_endpoint) {
                                        alert(__('Please configure the API endpoint in the payment settings.', 'youseeme'));
                                        return;
                                    }

                                    const $form = jQuery('form.woocommerce-checkout');
                                    
                                    if (!$form.length) {
                                        console.error('Checkout form not found');
                                        return;
                                    }

                                    // Trigger WooCommerce validation
                                    $form.trigger('checkout_place_order');
                                    
                                    // If validation passes, proceed with payment
                                    if ($form.triggerHandler('checkout_place_order_youseeme_card') !== false) {
                                        $form.addClass('processing').block({
                                            message: null,
                                            overlayCSS: {
                                                background: '#fff',
                                                opacity: 0.6
                                            }
                                        });

                                        // Get the payment data
                                        const paymentData = {
                                            action: 'youseeme_process_card_payment',
                                            nonce: youseemeCardData.nonce,
                                            payment_method: 'youseeme_card',
                                            youseeme_card_nonce: document.querySelector('#youseeme-card-nonce')?.value || ''
                                        };

                                        // Make the AJAX call
                                        jQuery.ajax({
                                            type: 'POST',
                                            url: youseemeCardData.ajax_url,
                                            data: paymentData,
                                            dataType: 'json',
                                            success: function(response) {
                                                $form.unblock();
                                                
                                                if (response.redirect) {
                                                    window.location.href = response.redirect;
                                                } else if (response.result === 'success') {
                                                    $form.submit();
                                                } else {
                                                    alert(response.messages || __('An error occurred. Please try again.', 'youseeme'));
                                                }
                                            },
                                            error: function(xhr) {
                                                $form.unblock();
                                                alert(__('Connection error. Please try again.', 'youseeme'));
                                                console.error('Payment error:', xhr.responseText);
                                            }
                                        });
                                    }
                                }}
                            >
                                {settings.title || __('Pay Now', 'youseeme')}
                            </button>
                        </div>
                        
                        <div className="youseeme-payment-logos">
                            <div className="youseeme-secure-payment">
                                <span className="dashicons dashicons-lock"></span>
                                {__('Secure Payment', 'youseeme')}
                            </div>
                            <div className="youseeme-accepted-cards">
                                <img 
                                    src={`${window.youseemeCardData?.plugin_url || ''}/assets/visa.svg`} 
                                    alt="Visa" 
                                    className="youseeme-card-logo" 
                                />
                                <img 
                                    src={`${window.youseemeCardData?.plugin_url || ''}/assets/mastercard.svg`} 
                                    alt="Mastercard" 
                                    className="youseeme-card-logo" 
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
